import { Event } from '../types/event.types';
import { calculateDistance } from './eventSorting';

interface Cluster {
  center: {
    lat: number;
    lng: number;
  };
  events: Event[];
}

export const CLUSTER_RADIUS = 0.25; // Base : 250m

export function clusterEvents(events: Event[], zoomLevel: number): Cluster[] {
  // Déclusterisation progressive plus agressive à haut zoom
  // Zoom 15: 250m, Zoom 16: 100m, Zoom 17: 40m, Zoom 18: 20m
  const baseRadius = CLUSTER_RADIUS;
  const zoomFactor = Math.pow(2.5, 15 - zoomLevel); // Changé de 1.4 à 2.5 pour déclusterisation plus rapide
  const radius = baseRadius * Math.max(zoomFactor, 0.02); // Min 20m (vs 120m)
  
  const clusters: Cluster[] = [];

  // Filtrer les événements sans coordonnées
  const validEvents = events.filter(
    event => event.latitude !== undefined && event.longitude !== undefined
  );

  validEvents.forEach(event => {
    const eventLat = event.latitude!;
    const eventLng = event.longitude!;

    // Chercher un cluster existant proche
    const existingCluster = clusters.find(cluster => {
      const distance = calculateDistance(
        cluster.center.lat,
        cluster.center.lng,
        eventLat,
        eventLng
      );
      return distance <= radius;
    });

    if (existingCluster) {
      // Ajouter l'événement au cluster existant
      existingCluster.events.push(event);
      // Recalculer le centre du cluster
      existingCluster.center = calculateClusterCenter(existingCluster.events);
    } else {
      // Créer un nouveau cluster
      clusters.push({
        center: { lat: eventLat, lng: eventLng },
        events: [event]
      });
    }
  });

  return clusters;
}

function calculateClusterCenter(events: Event[]): { lat: number; lng: number } {
  const validEvents = events.filter(
    event => event.latitude !== undefined && event.longitude !== undefined
  );

  const sum = validEvents.reduce(
    (acc, event) => ({
      lat: acc.lat + event.latitude!,
      lng: acc.lng + event.longitude!
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / validEvents.length,
    lng: sum.lng / validEvents.length
  };
}

interface MapBounds {
  sw: [number, number]; // [lng, lat] pour MapLibre
  ne: [number, number]; // [lng, lat] pour MapLibre
}

export function calculateMapBounds(
  events: Event[],
  userLocation?: { latitude: number; longitude: number }
): { bounds: MapBounds | undefined; zoom: number } {
  if (userLocation) {
    let minLat = userLocation.latitude;
    let maxLat = userLocation.latitude;
    let minLng = userLocation.longitude;
    let maxLng = userLocation.longitude;
    
    // Réduit à 10km
    const nearbyEvents = events.filter(event => {
      if (!event.latitude || !event.longitude) return false;
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        event.latitude,
        event.longitude
      );
      return distance <= 10; // 10km
    });

    if (nearbyEvents.length > 0) {
      nearbyEvents.forEach(event => {
        if (event.latitude! < minLat) minLat = event.latitude!;
        if (event.latitude! > maxLat) maxLat = event.latitude!;
        if (event.longitude! < minLng) minLng = event.longitude!;
        if (event.longitude! > maxLng) maxLng = event.longitude!;
      });
      
      return { 
        bounds: {
          sw: [minLng, minLat],
          ne: [maxLng, maxLat]
        }, 
        zoom: nearbyEvents.length === 1 ? 15 : 13 
      };
    }

    // Vue centrée sur l'utilisateur
    return { 
      bounds: {
        sw: [userLocation.longitude - 0.05, userLocation.latitude - 0.05],
        ne: [userLocation.longitude + 0.05, userLocation.latitude + 0.05]
      }, 
      zoom: 13 
    };
  }

  return { bounds: undefined, zoom: 13 };
}

export function calculateOptimalZoom(
  eventLocation: { latitude: number; longitude: number },
  userLocation?: { latitude: number; longitude: number }
): number {
  // Si pas de localisation utilisateur, zoom par défaut
  if (!userLocation) return 15;

  // Calculer la distance entre l'événement et l'utilisateur
  const distance = calculateDistance(
    eventLocation.latitude,
    eventLocation.longitude,
    userLocation.latitude,
    userLocation.longitude
  );

  // Définir le zoom en fonction de la distance
  if (distance < 0.5) return 16;      // Très proche (<500m)
  if (distance < 2) return 14;        // Distance de marche
  if (distance < 5) return 13;        // Distance vélo
  if (distance < 10) return 12;       // Courte distance en voiture
  if (distance < 20) return 10;       // Distance moyenne
  return 12;                          // Grande distance
}

export function calculateOptimalCenter(
  eventLocation: { latitude: number; longitude: number },
  userLocation?: { latitude: number; longitude: number }
): [number, number] {
  // Si pas de localisation utilisateur, centrer sur l'événement
  if (!userLocation) {
    return [eventLocation.latitude, eventLocation.longitude];
  }

  // Calculer le point médian
  return [
    (eventLocation.latitude + userLocation.latitude) / 2,
    (eventLocation.longitude + userLocation.longitude) / 2
  ];
}

// createMarkerIcon removed - using native MapLibre markers in MapLibreView
